import React, { useEffect, useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Info, Check } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatKWD } from "@/lib/format";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { MEASURE_FIELDS, CHECKOUT_NOTICE, TEXT_EN, fieldLabel } from "@/lib/measure";
import { COUNTRY_EN } from "@/lib/kuwaitAreas";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { lang, tr } = useLang();
  const nav = useNavigate();
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const first = items[0] || {};
  const c = first.customer || {};

  useEffect(() => {
    if (!c.country) { return; }
    api.post("/delivery/quote", { country: c.country, governorate: c.governorate || "", region: c.region || "" })
      .then((r) => { setQuote(r.data); setQuoteErr(false); })
      .catch(() => setQuoteErr(true));
  }, [c.country, c.governorate, c.region]);

  if (items.length === 0 && !submitted) return <Navigate to="/cart" replace />;

  const isPickup = c.country === "PICKUP";
  const fee = quote ? quote.fee : null;
  const total = fee != null ? Math.round((subtotal + fee) * 1000) / 1000 : null;
  const place = c.country === "KW" ? tr("الكويت", "Kuwait") : (lang === "en" ? COUNTRY_EN[c.country] || c.country : c.country);

  const submit = async () => {
    if (fee == null) { toast.error(tr("تعذّر احتساب رسوم التوصيل — عدّلي بيانات التوصيل", "Delivery fee unavailable — please edit your delivery details")); return; }
    setLoading(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id, color: i.color, color_ar: i.color_ar,
          measurements: i.measurements, notes: i.notes || "", measurement_confirmed: true,
        })),
        destination: { country: c.country, governorate: c.governorate || "", region: c.region || "", city: c.city || "", state: c.state || "", zip: c.zip || "", address: c.address, phone: c.phone, name: c.name, email: c.email || "" },
        payment_method: "knet",
        consents: { measurement: true, terms: true, alteration: true, terms_version: first.approvals?.terms_version || 1 },
      };
      const { data } = await api.post("/orders/checkout", payload);
      setSubmitted(true);
      clear();
      nav(`/pay/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 md:px-8 py-8 md:py-12 fade-in">
      <h1 className="font-display text-3xl mb-6">{tr("إتمام الطلب والدفع", "Checkout & payment")}</h1>
      <div className="flex items-start gap-2 bg-surface border border-border px-4 py-3 mb-6 text-xs leading-relaxed text-text-muted" data-testid="checkout-notice">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-accent" strokeWidth={1.5} />{tr(CHECKOUT_NOTICE, TEXT_EN.notice)}
      </div>

      <div className="bg-surface border border-border p-4 mb-4 text-sm space-y-1">
        <div className="font-medium mb-1">{isPickup ? tr("بيانات الاستلام", "Pickup details") : tr("بيانات التوصيل", "Delivery details")}</div>
        <div className="text-text-muted text-xs">{c.name} · <span dir="ltr">{c.phone}</span></div>
        <div className="text-text-muted text-xs">{`${place} / ${c.governorate ? c.governorate + " / " : ""}${c.region || c.city || ""} — ${c.address}`}</div>
        <Link to="/cart" className="text-xs text-accent underline" data-testid="checkout-edit-info">{tr("تعديل البيانات", "Edit details")}</Link>
      </div>

      {items.map((i) => (
        <div key={i.lid} className="border border-border p-3 mb-2 text-sm">
          <div className="flex justify-between"><span>{lang === "en" ? (i.name_en || i.name_ar) : i.name_ar} · {i.sku}</span><span dir="ltr">{formatKWD(i.price, lang)}</span></div>
          <div className="text-[11px] text-text-muted mt-1">{MEASURE_FIELDS.map((f) => `${fieldLabel(f, lang)} ${i.measurements[f.key]}`).join(" · ")} {tr("سم", "cm")}</div>
        </div>
      ))}

      <div className="mt-5">
        <div className="text-sm font-medium mb-2">{tr("طريقة الدفع", "Payment method")}</div>
        <div className="grid grid-cols-2 gap-2" data-testid="payment-methods">
          <div data-testid="checkout-pay-knet" className="relative min-h-[48px] px-3 py-3 text-sm border bg-[#171717] text-white border-[#171717]">
            <Check className="w-4 h-4 absolute top-2 end-2" strokeWidth={2.5} />{tr("كي نت (KNET)", "KNET")}
          </div>
        </div>
        <p className="text-[11px] text-text-muted mt-2">{tr("سيتم تحويلك إلى صفحة الدفع الإلكتروني (كي نت)؛ لن يبدأ التفصيل قبل تأكيد نجاح الدفع.", "You'll be taken to the KNET payment page; tailoring starts only after payment is confirmed.")}</p>
      </div>

      <div className="bg-surface p-5 mt-5 space-y-2 text-sm">
        <div className="flex justify-between"><span>{tr("مجموع القطع", "Items subtotal")}</span><span dir="ltr" data-testid="checkout-subtotal">{formatKWD(subtotal, lang)}</span></div>
        <div className="flex justify-between"><span>{isPickup ? tr("الاستلام من المحل", "Store pickup") : tr("التوصيل / الشحن (مرة واحدة للطلب)", "Delivery / shipping (once per order)")}</span>
          <span dir="ltr" data-testid="checkout-delivery">{fee != null ? formatKWD(fee, lang) : (quoteErr ? tr("غير متاح", "Unavailable") : "…")}</span></div>
        <div className="flex justify-between text-base font-medium border-t border-border pt-2"><span>{tr("الإجمالي", "Total")}</span>
          <span dir="ltr" data-testid="checkout-total">{total != null ? formatKWD(total, lang) : "—"}</span></div>
      </div>
      <div className="flex items-center gap-1 text-xs text-accent mt-3"><Check className="w-3.5 h-3.5" /> {tr("تم اعتماد المقاسات والموافقة على الشروط وسياسة التعديل خلال 7 أيام", "Measurements confirmed; terms and 7-day alteration policy accepted")}</div>

      <button onClick={submit} disabled={loading || fee == null} data-testid="place-order-btn" className="btn-primary w-full mt-5 py-4 text-sm tracking-wide disabled:opacity-50">
        {loading ? "…" : tr("المتابعة إلى الدفع (كي نت)", "Continue to payment (KNET)")}
      </button>
    </div>
  );
}
